import { useEffect, useState, useCallback, useMemo, type ReactNode, type MouseEvent } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Button,
  Menu,
  MenuItem,
  Stack,
  Chip,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import DateRangeSelector from '../../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import {
  PointhomeFunnelRetentionData,
  PointhomeFunnelDetailRetentionData,
} from '../../../types/pointhomeFunnel.types';
import PointhomeRetentionDashboard from '../../../components/pointhome/funnel/retention/PointhomeRetentionDashboard';
import PointhomeDetailRetentionDashboard from '../../../components/pointhome/funnel/retention/PointhomeDetailRetentionDashboard';

export default function PointhomeRetention() {
  const [retentionData, setRetentionData] = useState<PointhomeFunnelRetentionData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [activeSubTab, setActiveSubTab] = useState<number>(0);
  const [selectedPeriodPreset, setSelectedPeriodPreset] = useState<string>('D7');
  const [periodAnchorEl, setPeriodAnchorEl] = useState<null | HTMLElement>(null);
  const [osAnchorEl, setOsAnchorEl] = useState<null | HTMLElement>(null);
  const [serviceAnchorEl, setServiceAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [selectedServiceFilter, setSelectedServiceFilter] = useState<number>(0);
  const [detailRetentionDay, setDetailRetentionDay] = useState<number>(1);
  const [detailRetentionData, setDetailRetentionData] = useState<PointhomeFunnelDetailRetentionData>([]);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  const fetchFilterOptions = useCallback(() => {
    apiClient.get(`/api/v1/stat/pointhome/app/list`).then((response) => {
      setAppOptions(['앱 전체', ...response.data]);
    }).catch(() => {
      // 옵션 로드 실패 시에도 화면은 동작하도록 침묵 처리
    });
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const PERIOD_OPTIONS = useMemo(
    () => [
      { label: 'D7 (최근 1주)', value: 'D7', days: 7 },
      { label: 'D14 (최근 2주)', value: 'D14', days: 14 },
      { label: 'D21 (최근 3주)', value: 'D21', days: 21 },
      { label: 'D30 (최근 1달)', value: 'D30', days: 30 }
    ],
    []
  );

  const OS_OPTIONS = useMemo(
    () => [
      { label: 'AOS + IOS', value: 'all' },
      { label: 'AOS', value: 'aos' },
      { label: 'IOS', value: 'ios' },
    ],
    []
  );

  const SERVICE_FILTER_OPTIONS = useMemo(
    () => [
      { label: '포인트홈 + 룰렛 + 오퍼월', value: 0 },
      { label: '포인트홈', value: 1 },
      { label: '룰렛', value: 2 },
      { label: '오퍼월', value: 3 },
      { label: '포인트홈 + 룰렛', value: 4 },
      { label: '포인트홈 + 오퍼월', value: 5 },
      { label: '룰렛 + 오퍼월', value: 6 },
    ],
    []
  );

  const applyPeriodPreset = useCallback(
    (value: string) => {
      setSelectedPeriodPreset(value);
      const found = PERIOD_OPTIONS.find((option) => option.value === value);
      if (found?.days) {
        const newEnd = dayjs().subtract(1, 'day');
        const newStart = newEnd.subtract(found.days, 'day');
        setStartDate(newStart);
        setEndDate(newEnd);
      }
    },
    [PERIOD_OPTIONS]
  );

  const fetchRetentionSummaryData = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } : {}),
      os_type: selectedOs,
      service_filter: String(selectedServiceFilter),
    };

    apiClient
      .get(`/api/v1/analytics/pointhome/retention`, { params })
      .then((response) => {
        setRetentionData(response.data);
        setErrorMessage('');
      })
      .catch(() => {
        setRetentionData(null);
        setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs, selectedServiceFilter]);

  const fetchDetailRetentionData = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setDetailLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      retention_days: String(detailRetentionDay),
      app: selectedApp === '앱 전체' ? 'all' : selectedApp,
      os_type: selectedOs,
      service_filter: String(selectedServiceFilter),
    };

    apiClient
      .get(`/api/v1/analytics/pointhome/retention/detail`, { params })
      .then((response) => {
        setDetailRetentionData(response.data);
      })
      .catch(() => {
        setDetailRetentionData([]);
      })
      .finally(() => setDetailLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs, selectedServiceFilter, detailRetentionDay]);

  const handleChangeDetailRetentionDay = (day: number) => {
    setDetailRetentionDay(day);
  };

  useEffect(() => {
    applyPeriodPreset('D7');
    setInitialized(true);
  }, [applyPeriodPreset]);

  useEffect(() => {
    if (!initialized || !startDate || !endDate || selectedApp == null) return;

    // 요약 탭 데이터만 자동 갱신
    fetchRetentionSummaryData();
  }, [
    initialized,
    startDate,
    endDate,
    selectedApp,
    selectedOs,
    selectedServiceFilter,
    fetchRetentionSummaryData
  ]);

  // 상세 탭 활성 상태에서 D-day 또는 필터/날짜가 바뀔 때만 상세 API 호출
  useEffect(() => {
    if (
      !initialized ||
      activeSubTab !== 1 ||
      !startDate ||
      !endDate ||
      selectedApp == null
    ) {
      return;
    }
    fetchDetailRetentionData();
  }, [
    initialized,
    activeSubTab,
    startDate,
    endDate,
    selectedApp,
    selectedOs,
    selectedServiceFilter,
    detailRetentionDay,
    fetchDetailRetentionData,
  ]);

  const handleStartDateChange = (value: Dayjs | null) => {
    setSelectedPeriodPreset('custom');
    setStartDate(value);
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    setSelectedPeriodPreset('custom');
    setEndDate(value);
  };

  const selectedPeriodLabel =
    PERIOD_OPTIONS.find((option) => option.value === selectedPeriodPreset)?.label ?? 'D7 (최근 1주)';
  const selectedOsLabel =
    OS_OPTIONS.find((option) => option.value === selectedOs)?.label ?? 'OS 전체';
  const selectedServiceLabel =
    SERVICE_FILTER_OPTIONS.find((option) => option.value === selectedServiceFilter)?.label ?? '포인트홈 + 룰렛 + 오퍼월';

  const renderFilterButton = ({
    icon,
    label,
    value,
    onClick,
  }: {
    icon: ReactNode;
    label: string;
    value: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  }) => (
    <Button
      variant="outlined"
      onClick={onClick}
      endIcon={<KeyboardArrowDownIcon />}
      sx={{
        borderColor: '#d0d7e2',
        borderRadius: 2,
        textTransform: 'none',
        color: '#1f2a37',
        minWidth: 220,
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon}
        <Stack direction="column" alignItems="flex-start">
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 12 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Button>
  );

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      {/* 상단 필터 영역 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onSearch={() => {
              if (startDate && endDate) fetchRetentionSummaryData();
            }}
          />
        </Box>

        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(v) => setSelectedApp(v)}
            onSearch={fetchRetentionSummaryData}
          />
        </Box>
      </Box>

      {/* 대시보드 박스 */}
      <Box sx={{ ...PAGE_CARD_SX, width: '100%' }}>
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {/* 하위 탭들 */}
        <Tabs
          value={activeSubTab}
          onChange={(_, newValue) => {
            setActiveSubTab(newValue);
            // 상세 조회 탭으로 전환될 때만 상세 API 호출
            if (
              newValue === 1 &&
              startDate &&
              endDate &&
              selectedApp != null
            ) {
              fetchDetailRetentionData();
            }
          }}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 36,
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 'normal',
              px: 2,
              py: 1,
              borderRadius: '8px',
              mr: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 68, 210, 0.1)',
                color: '#0044D2',
                fontWeight: 'bold'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          <Tab label="전체 요약" />
          <Tab label="상세 조회" />
        </Tabs>

        <Box mt={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {renderFilterButton({
              icon: <CalendarTodayOutlinedIcon fontSize="small" />,
              label: '조회 기간',
              value: selectedPeriodLabel,
              onClick: (event) => setPeriodAnchorEl(event.currentTarget),
            })}
            {renderFilterButton({
              icon: <LaptopMacOutlinedIcon fontSize="small" />,
              label: 'OS',
              value: selectedOsLabel,
              onClick: (event) => setOsAnchorEl(event.currentTarget),
            })}
            {renderFilterButton({
              icon: <EmojiObjectsOutlinedIcon fontSize="small" />,
              label: '서비스',
              value: selectedServiceLabel,
              onClick: (event) => setServiceAnchorEl(event.currentTarget),
            })}
          </Stack>

          <Menu
            anchorEl={periodAnchorEl}
            open={Boolean(periodAnchorEl)}
            onClose={() => setPeriodAnchorEl(null)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedPeriodPreset}
                onClick={() => {
                  setPeriodAnchorEl(null);
                  applyPeriodPreset(option.value);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={osAnchorEl}
            open={Boolean(osAnchorEl)}
            onClose={() => setOsAnchorEl(null)}
          >
            {OS_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedOs}
                onClick={() => {
                  setOsAnchorEl(null);
                  setSelectedOs(option.value);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={serviceAnchorEl}
            open={Boolean(serviceAnchorEl)}
            onClose={() => setServiceAnchorEl(null)}
          >
            {SERVICE_FILTER_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedServiceFilter}
                onClick={() => {
                  setServiceAnchorEl(null);
                  setSelectedServiceFilter(option.value);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {retentionData ? `총 ${retentionData.length}일 데이터가 조회되었습니다.` : '데이터가 없습니다.'}
          </Typography>
        </Box>
  
        {/* 대시보드 / 로딩 / 에러 처리 */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress size={40} sx={{ color: '#0044D2' }} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : retentionData && retentionData.length > 0 && startDate && endDate ? (
          activeSubTab === 0 ? (
            <PointhomeRetentionDashboard 
              data={retentionData} 
              selectedApp={selectedApp}
              startDate={startDate}
              endDate={endDate}
            />
          ) : (
            <PointhomeDetailRetentionDashboard
              detailRetentionDay={detailRetentionDay}
              onChangeDetailRetentionDay={handleChangeDetailRetentionDay}
              detailRetentionData={detailRetentionData}
              detailLoading={detailLoading}
            />
          )
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            데이터가 없습니다.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
